import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { Order } from "./use-orders";

export interface ProjectDetails {
  imageUrl: string | null;
  thumbnailUrl: string | null;
  json: any | null;
  physicalProduct: {
    sku: string;
    size_description: string;
  } | null;
}

export interface OrderWithDetails extends Order {
  project_details?: Record<string, ProjectDetails>;
}

export const useOrder = (orderId: string | undefined) => {
  const queryClient = useQueryClient();
  const { client, session } = useAuthContext();

  // First check if order is in cache from the orders list
  const getCachedOrder = (): Order | undefined => {
    if (!orderId) return undefined;

    const cachedData = queryClient.getQueriesData<{ orders: Order[] }>({
      queryKey: ["admin-orders"],
    });

    for (const [, data] of cachedData) {
      if (data?.orders) {
        const order = data.orders.find((o) => o.id === orderId);
        if (order) return order;
      }
    }

    return undefined;
  };

  const fetchOrder = async (): Promise<OrderWithDetails> => {
    if (!client) throw new Error("useOrder: Client not initialized");
    if (!session) throw new Error("useOrder: No active session");
    if (!orderId) throw new Error("useOrder: Order ID is required");

    const { data, error } = await client.functions.invoke("admin-get-order", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: { orderId },
    });

    if (error) throw error;
    if (!data?.order) throw new Error("Order not found");

    return data.order;
  };

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ["admin-order", orderId],
    queryFn: fetchOrder,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !!session && !!orderId,
    placeholderData: () => {
      // Use cached order from list as placeholder while fetching full details
      const cached = getCachedOrder();
      return cached ? { ...cached, project_details: {} } : undefined;
    },
  });

  return {
    order: data,
    isLoading,
    isFetching,
    isError,
    error,
  };
};
